import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import {
  ASSIGNABLE_ROLES,
  auditLabels,
  can,
  roleHints,
  roleLabel,
  roleNames,
  type Role,
} from '@zilian/contracts';
import { api } from '../api';
import { Empty, Heading, Props, Tag, type Field } from '../ui';
import PaymentProfileCard from '../PaymentProfileCard';

/** Roles as checkboxes, each with what it opens in one line; 只读 only for members who have it. */
const rolesField = (current: string[] = []): Field => ({
  name: 'roles',
  label: '岗位（可多选）',
  multi: true,
  options: [...ASSIGNABLE_ROLES, ...(current.includes('viewer') ? (['viewer'] as Role[]) : [])].map(
    (value) => ({ value, label: roleNames[value], hint: roleHints[value] }),
  ),
});

// Warehouses are kept under 库存, with the stock they hold.
type Section = 'members' | 'payment' | 'audit';
const SECTIONS: { value: Section; label: string; subtitle: string }[] = [
  { value: 'members', label: '企业成员', subtitle: '成员账号、岗位与小程序登录。' },
  { value: 'payment', label: '收款信息', subtitle: '客户付款时看到的账户和收款码。' },
  { value: 'audit', label: '操作记录', subtitle: '最近 200 条关键操作，按时间倒序。' },
];

/**
 * 企业设置, split by what people come here to do: who works here and what they may see, how
 * customers pay, and the record of key actions. Each member sees only the
 * sections their roles use (one section: no tabs at all); the open section's main action sits
 * in the heading, and the section is kept in the address so a refresh stays put.
 */
export function Settings(p: Props) {
  const roles: string[] = p.current?.roles ?? [];
  const isAdmin = roles.includes('admin');
  const visible = SECTIONS.filter((s) =>
    s.value === 'payment' ? can(roles, 'price.sale') || can(roles, 'price.purchase') : isAdmin,
  );
  const [params, setParams] = useSearchParams();
  const section = visible.find((s) => s.value === params.get('tab')) ?? visible[0] ?? SECTIONS[1];
  const go = (value: Section) => setParams({ tab: value }, { replace: true });

  const action =
    section.value === 'members' ? (
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() =>
          p.open({
            title: '新增企业成员',
            fields: [
              { name: 'username', label: '登录账号' },
              { name: 'name', label: '成员姓名' },
              { name: 'password', label: '初始密码（至少12位）', type: 'password', wide: true },
              rolesField(),
            ],
            form: { username: '', name: '', password: '', roles: [] },
            submit: (f: any) => api('/members', f),
          })
        }
      >
        新增成员
      </Button>
    ) : undefined;

  return (
    <>
      <Heading
        title="企业设置"
        subtitle={visible.length > 1 ? undefined : section.subtitle}
        action={action}
      />
      {visible.length > 1 && (
        <Box sx={{ borderBottom: '1px solid #e7edef', mb: 2.5, mt: -1 }}>
          <Tabs value={section.value} onChange={(_, v) => go(v)}>
            {visible.map((s) => (
              <Tab key={s.value} value={s.value} label={s.label} />
            ))}
          </Tabs>
        </Box>
      )}
      {section.value === 'members' && <Members {...p} />}
      {section.value === 'payment' && (
        <PaymentProfileCard
          workspace={p.workspace}
          canEdit={roles.some((r) => r === 'admin' || r === 'finance')}
          notify={p.notify}
        />
      )}
      {section.value === 'audit' && <AuditLog {...p} />}
    </>
  );
}

/** Members: one row each, one management entry per row. */
function Members(p: Props) {
  const [members, setMembers] = useState<any[] | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; member: any } | null>(null);
  const [resetting, setResetting] = useState<any>(null);
  useEffect(() => {
    let live = true;
    api<any[]>('/members')
      .then((m) => live && setMembers(m))
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [p.version, p.workspace]);

  const editRoles = (m: any) =>
    p.open({
      title: '修改岗位',
      description: `${m.display_name}（${m.username}），保存后立即生效，无需重新登录。`,
      fields: [rolesField(m.roles)],
      form: { roles: m.roles },
      submit: (f: any) => api(`/members/${m.id}/roles`, { roles: f.roles }),
    });
  // Frees the account from its WeChat, e.g. a new phone or a WeChat used for another account.
  const resetWechat = (m: any) =>
    p.open({
      title: '重置微信绑定',
      description: `解除 ${m.display_name} 与当前微信的绑定并退出其小程序登录。之后用任意微信登录，输入账号和密码即可重新绑定。`,
      fields: [],
      form: {},
      confirmLabel: '确认重置',
      submit: () => api(`/members/${m.id}/wechat/reset`, {}),
    });
  const pick = (run: (m: any) => void) => () => {
    const member = menu!.member;
    setMenu(null);
    run(member);
  };

  if (!members) return null;
  return (
    <Paper>
      <TableContainer>
        <Table sx={{ '& th, & td': { px: 2.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell>成员</TableCell>
              <TableCell>岗位</TableCell>
              <TableCell>小程序</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id}>
                <TableCell sx={{ width: '34%' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{m.display_name}</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    {m.username}
                  </Typography>
                </TableCell>
                <TableCell>{roleLabel(m.roles)}</TableCell>
                <TableCell>
                  {m.wechat_bound ? (
                    <Tag tone="success">已绑定微信</Tag>
                  ) : (
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      未绑定
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  <Button
                    size="small"
                    endIcon={<MoreHorizOutlined />}
                    onClick={(e) => setMenu({ anchor: e.currentTarget, member: m })}
                  >
                    管理
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem sx={{ minWidth: 220 }} onClick={pick(editRoles)}>
          <ListItemText primary="修改岗位" />
        </MenuItem>
        <MenuItem onClick={pick(setResetting)}>
          <ListItemText primary="重置密码" secondary="发链接给本人设置新密码" />
        </MenuItem>
        <MenuItem disabled={!menu?.member.wechat_bound} onClick={pick(resetWechat)}>
          <ListItemText
            primary="重置微信绑定"
            secondary={menu?.member.wechat_bound ? '换手机或绑错微信时使用' : '尚未绑定微信'}
          />
        </MenuItem>
      </Menu>
      {resetting && (
        <ResetPasswordDialog
          member={resetting}
          onClose={() => setResetting(null)}
          onIssued={() => p.reload()}
        />
      )}
    </Paper>
  );
}

/**
 * A forgotten password: the administrator gets a one-time link (24 hours) and sends it to the
 * member, who sets a new password themselves. The administrator never sees or chooses it.
 */
function ResetPasswordDialog(p: { member: any; onClose: () => void; onIssued: () => void }) {
  const [link, setLink] = useState<{ link: string; expiresAt: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const issue = async () => {
    setBusy(true);
    setError('');
    try {
      setLink(await api(`/members/${p.member.id}/password-link`, {}));
      p.onIssued();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link!.link);
      setCopied(true);
    } catch {
      setError('浏览器不允许自动复制，请选中链接后手动复制');
    }
  };
  const until = link && new Date(link.expiresAt).toLocaleString('zh-CN', { hour12: false });
  return (
    <Dialog open onClose={() => !busy && p.onClose()} fullWidth maxWidth="sm">
      <DialogTitle>重置密码</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!link ? (
          <Typography sx={{ fontSize: 14, lineHeight: 1.8 }}>
            为 {p.member.display_name} 生成一个设置新密码的链接，由本人打开后设置。
            <Typography
              component="span"
              color="text.secondary"
              sx={{ display: 'block', fontSize: 13 }}
            >
              链接 24 小时内有效、只能用一次；设置新密码后，旧密码和已登录的设备都会失效。
            </Typography>
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, mb: 1.5 }}>
              通过微信发给 {p.member.display_name}，手机或电脑打开均可。
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                bgcolor: '#f5f7f8',
                border: '1px solid #e7edef',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 13,
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {link.link}
            </Box>
            <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
              {until} 前有效。再次生成会让这条链接失效。
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!link ? (
          <>
            <Button onClick={p.onClose} disabled={busy}>
              取消
            </Button>
            <Button variant="contained" onClick={issue} disabled={busy}>
              {busy ? '正在生成…' : '生成链接'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={p.onClose}>完成</Button>
            <Button variant="contained" onClick={copy}>
              {copied ? '已复制' : '复制链接'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Who did what, in words; the record it touched by its short number (full one on hover). */
function AuditLog(p: Props) {
  const [logs, setLogs] = useState<any[] | null>(null);
  useEffect(() => {
    let live = true;
    api<any[]>('/audit')
      .then((l) => live && setLogs(l))
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [p.version, p.workspace]);
  if (!logs) return null;
  if (!logs.length) return <Empty>还没有操作记录。</Empty>;
  return (
    <Paper>
      <TableContainer>
        <Table sx={{ '& th, & td': { px: 2.5 }, '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 180 }}>时间</TableCell>
              <TableCell sx={{ width: 160 }}>操作人</TableCell>
              <TableCell>操作</TableCell>
              <TableCell>对象</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id}>
                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {new Date(l.created_at).toLocaleString('zh-CN', { hour12: false })}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.display_name}</TableCell>
                <TableCell>{auditLabels[l.action] ?? l.action}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                  {l.detail?.username ?? l.detail?.name ?? (
                    <Tooltip title={l.entity_id}>
                      <span>{String(l.entity_id).slice(0, 8)}</span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
